import { Platform } from 'react-native';

import { type MachineImplementationsFrom } from 'xstate';

import { datadog } from '@sg/expo-datadog-client';

import { type otaMachine } from '../ota-machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the OTA machine's external actions so that every consumer reports the
 * same update outcomes. Only outcomes and failures are logged; routine
 * lifecycle steps stay silent.
 */
export const createOtaMachineReporter = (): OtaMachineActions => {
  // ─── Helpers ─────────────────────────────────────────────────────────

  const report = (params: ReportParams) => {
    const { event, level = 'info', attributes } = params;

    if (Platform.OS === 'web') return;

    const datadogMessage = [`[OTA]`, event].join(' ');

    try {
      datadog.logger[level](datadogMessage, { event: `ota.${event}`, ...attributes });
    } catch {}
  };

  const onLaunchReconciled: LaunchOutcomeReporter = ({ context }) => {
    const { launchOutcome } = context;

    if (launchOutcome.kind === 'update-applied') {
      report({ event: 'update_applied', attributes: launchOutcome });
    }

    if (launchOutcome.kind === 'install-failed') {
      report({ event: 'update_install_failed', level: 'warn', attributes: launchOutcome });
    }
  };

  // ─────────────────────────────────────────────────────────────────────

  return {
    onLaunchReconciled,
    onLaunchGateTimedOut: () => report({ event: 'launch_gate_timed_out', level: 'warn' }),
    onLaunchFetchFailed: () => report({ event: 'launch_fetch_failed', level: 'warn' }),
    onLaunchFetchTimedOut: () => report({ event: 'launch_fetch_timed_out', level: 'warn' }),
    onRestartFailed: () => report({ event: 'restart_failed', level: 'error' }),
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ReportParams = {
  event: string;
  level?: keyof typeof datadog.logger;
  attributes?: Record<string, unknown>;
};

type OtaMachineActions = NonNullable<MachineImplementationsFrom<typeof otaMachine>['actions']>;

type LaunchOutcomeReporter = OtaMachineActions['onLaunchReconciled'];
